import 'driver.js/dist/driver.css';
import './product-tour.css';

import { driver } from 'driver.js';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import fleetApi from '../../services/api.service';
import { useIsMobile } from '../../utils/useIsMobile';
import { PRODUCT_TOUR_STEPS } from './product-tour-steps';

// Paramètre d'URL permettant de relancer la visite (bouton « Visite guidée »)
export const PRODUCT_TOUR_QUERY_PARAM = 'tour';
// Laisse le temps au tableau de bord et au menu de se rendre
const START_DELAY_MS = 800;

// La visite vue est gardée dans la config de l'utilisateur (users.config.product_tour_seen)
const markTourAsSeen = async () => {
  try {
    await fleetApi.put('/users/config/product-tour');
  } catch (error) {
    console.error(
      "Erreur lors de l'enregistrement de la visite guidée :",
      error
    );
  }
};

const startTour = (onClosedByUser) => {
  const steps = PRODUCT_TOUR_STEPS.filter(
    (step) => !step.element || document.querySelector(step.element)
  );

  const tour = driver({
    steps,
    showProgress: true,
    progressText: '{{current}} sur {{total}}',
    nextBtnText: 'Suivant',
    prevBtnText: 'Précédent',
    doneBtnText: 'Terminer',
    popoverClass: 'fleet-product-tour',
    stagePadding: 6,
    stageRadius: 6,
    // Évite de quitter le tableau de bord en cliquant sur un lien du menu
    disableActiveInteraction: true,
    // Terminer, croix, Échap ou clic sur le fond. Un appel à destroy() par
    // l'application (changement de page, démontage) ne passe pas par ce hook
    onDestroyStarted: () => {
      tour.destroy();
      onClosedByUser();
    },
  });

  tour.drive();
  return tour;
};

/**
 * Visite guidée de l'application, lancée automatiquement sur le tableau de
 * bord tant que l'utilisateur ne l'a pas vue (desktop uniquement), ou à la
 * demande via `/?tour=1`.
 * `blocked` permet de différer la visite tant que la modale ou le bandeau
 * d'onboarding de configuration (entrepôts / véhicules) sont affichés.
 */
export default function ProductTour({ isAuthenticated, blocked }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const tourRef = useRef(null);
  const isForcedRef = useRef(false);
  // null tant que la config de l'utilisateur n'est pas chargée
  const [hasSeenTour, setHasSeenTour] = useState(null);

  const isOnDashboard = location.pathname === '/';
  const isRequested =
    new URLSearchParams(location.search).get(PRODUCT_TOUR_QUERY_PARAM) === '1';

  useEffect(() => {
    if (!isAuthenticated) return;

    let isCancelled = false;
    const fetchHasSeenTour = async () => {
      try {
        const { data } = await fleetApi.get('/users/me');
        if (!isCancelled) {
          setHasSeenTour(data?.config?.product_tour_seen === true);
        }
      } catch {
        // Sans config, la visite ne se lance pas d'elle-même (elle reste disponible via le bouton)
        if (!isCancelled) setHasSeenTour(true);
      }
    };
    fetchHasSeenTour();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isRequested) {
      // Retire le paramètre de l'URL, la visite démarre au rendu suivant
      isForcedRef.current = true;
      navigate('/', { replace: true });
      return;
    }
    if (!isAuthenticated || !isOnDashboard || isMobile || blocked) return;
    if (!isForcedRef.current && hasSeenTour !== false) return;

    const timeoutId = setTimeout(() => {
      isForcedRef.current = false;
      if (!tourRef.current?.isActive()) {
        // Terminée ou fermée par l'utilisateur (croix, Échap), la visite est vue :
        // il peut la relancer depuis le bouton « Visite guidée »
        tourRef.current = startTour(() => {
          setHasSeenTour(true);
          markTourAsSeen();
        });
      }
    }, START_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [
    isAuthenticated,
    isOnDashboard,
    isMobile,
    blocked,
    hasSeenTour,
    isRequested,
    navigate,
  ]);

  // Ferme la visite, sans la marquer vue, si l'utilisateur quitte le tableau de
  // bord ou si le composant est démonté (déconnexion)
  useEffect(() => {
    const closeTour = () => {
      if (tourRef.current?.isActive()) tourRef.current.destroy();
    };

    if (!isOnDashboard) closeTour();
    return closeTour;
  }, [isOnDashboard]);

  return null;
}
